export enum UserPermissions {
  None = 0,
  IsAdmin = 1,
  CanEditLicense = 2,
  CanReprocess = 4,

  /// Can trigger webhook logs cleaning
  CanCleanLogs = 8,
}

/**
 * Check if a user has a specific permission
 * @param userPermissions - User's permissions value (bitwise)
 * @param requiredPermission - The permission to check
 * @returns true if user has the permission or is admin
 */
export function hasPermission(
  userPermissions: number,
  requiredPermission: UserPermissions
): boolean {
  // Admin has all permissions
  if ((userPermissions & UserPermissions.IsAdmin) === UserPermissions.IsAdmin) {
    return true;
  }

  // Check specific permission
  return (userPermissions & requiredPermission) === requiredPermission;
}

/**
 * Check if a user is an administrator
 * @param userPermissions - User's permissions value (bitwise)
 * @returns true if user has IsAdmin permission
 */
export function isAdmin(userPermissions: number): boolean {
  return (userPermissions & UserPermissions.IsAdmin) === UserPermissions.IsAdmin;
}

/**
 * Combine multiple permissions into a single value
 * @param permissions - Array of permissions to combine
 * @returns Combined permissions value
 */
export function combinePermissions(...permissions: UserPermissions[]): number {
  return permissions.reduce((acc, permission) => acc | permission, 0);
}

/**
 * Get all permissions a user has as an array
 * @param userPermissions - User's permissions value (bitwise)
 * @returns Array of permission enums the user has
 */
export function getPermissions(userPermissions: number): UserPermissions[] {
  const permissions: UserPermissions[] = [];

  if ((userPermissions & UserPermissions.IsAdmin) === UserPermissions.IsAdmin) {
    permissions.push(UserPermissions.IsAdmin);
  }
  if ((userPermissions & UserPermissions.CanEditLicense) === UserPermissions.CanEditLicense) {
    permissions.push(UserPermissions.CanEditLicense);
  }
  if ((userPermissions & UserPermissions.CanReprocess) === UserPermissions.CanReprocess) {
    permissions.push(UserPermissions.CanReprocess);
  }
  if ((userPermissions & UserPermissions.CanCleanLogs) === UserPermissions.CanCleanLogs) {
    permissions.push(UserPermissions.CanCleanLogs);
  }

  return permissions;
}
